import React, { useEffect, useRef, useState } from "react";
import { ProgressBar } from "react-bootstrap";
import "./CustomTabs.css";

interface TabData {
  name: string;
  redirectUrl?: string;
  disabled?: boolean;
  content: React.ReactNode;
  progress?: number;
  loadingProgressBar?: boolean;
  badge?: string | number;
}

interface CustomTabsProps {
  data: TabData[];
}

const CustomTabs: React.FC<CustomTabsProps> = ({ data }) => {
  const [activeTab, setActiveTab] = useState<any>(0); // Default is none active
  const tabsRef = useRef<HTMLDivElement | null>(null); // Store refs to buttons
  const [isOverflow, setIsOverflow] = useState(false);
  const overflowRef = useRef(false);
  const buttonsRef = useRef<(HTMLDivElement | null)[]>([]); // Store refs to buttons
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({}); // Style for the indicator

  const handleButtonClick = (tabName: string, index: number) => {
    setActiveTab(index); // Update the active tab
    const button = buttonsRef.current[index]; // Get the clicked button element
    if (button) {
      // Set the width and left position of the indicator
      setIndicatorStyle({
        width: `${button.offsetWidth}px`,
        left: `${button.offsetLeft}px`,
      });
    }
  };

  const checkOverflow = () => {
    if (tabsRef.current) {
      const clientWidth = tabsRef.current.clientWidth;
      const scrollWidth = tabsRef.current.scrollWidth;
      const isCurrentlyOverflowing = scrollWidth > clientWidth;
      setIsOverflow(isCurrentlyOverflowing);
    }
  };

  useEffect(() => {
    // Initially set the indicator to the first button's position (if necessary)
    if (buttonsRef.current[0]) {
      setIndicatorStyle({
        width: `${buttonsRef.current[0]!.offsetWidth}px`,
        left: `${buttonsRef.current[0]!.offsetLeft}px`,
      });
    }
  }, []);

  const handleResize = () => {
    checkOverflow();
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    checkOverflow();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const scrollTabs = (direction: "left" | "right") => {
    if (tabsRef.current) {
      const scrollAmount = 100;
      if (direction === "left") {
        tabsRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      } else {
        tabsRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    }
  };

  return (
    <>
      <div className="desktop-main-content">
        {isOverflow && (
          <button className="nav-btn" onClick={() => scrollTabs("left")}>
            &#9664;
          </button>
        )}

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          <div
            className="desktop-main-content-display"
            ref={(node) => {
              tabsRef.current = node;
            }}
          >
            {data.map((tab, index) => (
              <div key={index}>
                {tab.name != "" && (
                  <div
                    className={`${tab.disabled ? "disabledTabs" : ""
                      } desktop-main-content-buttons`}
                    ref={(el) => (buttonsRef.current[index] = el)}
                    onClick={() => handleButtonClick(tab.name, index)}
                  >
                    {tab.name}
                    {tab.badge !== undefined && (
                      <span
                        style={{
                          marginLeft: "6px",
                          display: "flex",
                          justifyContent: "center",
                          gap: "6px",
                          minWidth: "24px",
                          alignItems: "center",
                        }}
                        className={`badge rounded-pill bg-${tab.name.toLowerCase() === "closed"
                          ? "secondary"
                          : "primary"
                          }`}
                      >
                        {tab.badge}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="horizontal-indicator" style={indicatorStyle}></div>
          </div>
          <>
            {data.map((tab, index) => {
              return (
                <div key={index}>
                  {tab.progress && tab.loadingProgressBar && (
                    <div style={{ width: "100%" }}>
                      <ProgressBar
                        style={{ height: "6px", backgroundColor: 'var(--cards-bg-color)' }}
                        animated
                        striped
                        now={tab.progress}
                      ></ProgressBar>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        </div>

        {isOverflow && (
          <button className="nav-btn" onClick={() => scrollTabs("right")}>
            &#9654;
          </button>
        )}
      </div>

      {/* Conditional rendering based on the active tab */}
      <div className="content-display">
        {data.map((tab, index) =>
          index === activeTab ? (
            <div key={index}>
              {tab.content ? (
                typeof tab.content === "string" ? (
                  <div>{tab.content}</div>
                ) : (
                  tab.content
                )
              ) : (
                <div></div>
              )}
            </div>
          ) : null
        )}
      </div>
    </>
  );
};

export default CustomTabs;
